/**
 * Web Audio realisations of the `EffectChain` model.
 *
 * The model is shared with the native build, where every one of its 24 kinds runs live on an
 * AVAudioUnit. The web cannot match that, and this module's first job is to be honest about
 * which ones it can: `EFFECT_REALTIME_SUPPORT` classifies every kind as a realtime node, a
 * parameter the *voice* applies, or an offline render, and the UI reads that table so a knob
 * is never shown as live when it is not.
 *
 * Nothing here fetches anything. The reverb impulse response is synthesised from shaped noise
 * at the sample rate the context is actually running at, which is both smaller than shipping
 * a set of IR files and correct at 44.1 kHz and 48 kHz without resampling.
 *
 * Every unit is wired the same way so bypass is uniform and click-free:
 *
 *     input ─┬─► (effect internals) ─► active ─┬─► output
 *            └───────────────────────► bypass ─┘
 *
 * `active` and `bypass` are crossfaded with `setTargetAtTime`. Disconnecting a live node
 * instead would put a step in the waveform, which is a click, which is the one thing an
 * instrument may not do.
 */

import type { EffectChain, EffectKind, EffectNode, UUID } from '../model/types';
import {
  EFFECT_KINDS,
  effectKindParameter,
  effectKindParameters,
  effectParameterClamp,
} from '../model/types';

/** How an effect kind can be reproduced in the browser in real time. */
export type EffectRealtimeSupport =
  /** Built from Web Audio nodes and inserted into the pad's strip. */
  | 'node'
  /** Not a node at all — folded into the voice's read rate when it is triggered. */
  | 'voice'
  /** Needs a render pass; the analysis worker bounces it into a new buffer. */
  | 'offline';

/**
 * The whole table, in one place, because both the UI and the engine have to agree about it.
 *
 * The offline group is not a shortcut. Bit crushing, decimation, stutter and glitch all need
 * per-sample state that no native node exposes; formant shift, harmonising, pitch correction
 * and time stretch need a phase vocoder. Each would take a dedicated AudioWorklet processor
 * per pad, and PadLab already spends its worklet budget on voices, metering and the limiter —
 * so they are rendered once, into a new buffer, by the worker.
 */
export const EFFECT_REALTIME_SUPPORT: Readonly<Record<EffectKind, EffectRealtimeSupport>> =
  Object.freeze({
    eq: 'node',
    highPass: 'node',
    lowPass: 'node',
    bandPass: 'node',
    compressor: 'node',
    limiter: 'node',
    noiseGate: 'offline',
    distortion: 'node',
    bitCrush: 'offline',
    sampleRateReduce: 'offline',
    ringMod: 'node',
    reverb: 'node',
    delay: 'node',
    chorus: 'node',
    flanger: 'node',
    tremolo: 'node',
    pitchShift: 'voice',
    formantShift: 'offline',
    timeStretch: 'offline',
    harmoniser: 'offline',
    autoTune: 'offline',
    stutter: 'offline',
    glitch: 'offline',
    gainTrim: 'node',
  } satisfies Record<EffectKind, EffectRealtimeSupport>);

export function effectKindRealtimeSupport(kind: EffectKind): EffectRealtimeSupport {
  return EFFECT_REALTIME_SUPPORT[kind];
}

/** Kinds that can be heard while you play. Everything else needs a bounce. */
export const REALTIME_EFFECT_KINDS: readonly EffectKind[] = EFFECT_KINDS.filter(
  (kind) => EFFECT_REALTIME_SUPPORT[kind] !== 'offline',
);

export const OFFLINE_ONLY_EFFECT_KINDS: readonly EffectKind[] = EFFECT_KINDS.filter(
  (kind) => EFFECT_REALTIME_SUPPORT[kind] === 'offline',
);

/**
 * A sentence the UI can put next to an offline-only effect. Written for a player, not an
 * engineer, and never says "unsupported" when the effect is available — just later.
 */
export function effectKindRealtimeNote(kind: EffectKind): string | null {
  switch (EFFECT_REALTIME_SUPPORT[kind]) {
    case 'node':
      return null;
    case 'voice':
      return 'Applies from the next time you hit the pad.';
    case 'offline':
      return 'Renders into the clip when you bounce. You will not hear it while you play.';
  }
}

/** Realtime pitch is applied by the voice, so a chain can tell the engine how far to shift. */
export function chainVoicePitchCents(chain: EffectChain): number {
  if (chain.isBypassed) return 0;
  let cents = 0;
  for (const node of chain.nodes) {
    if (node.isBypassed || node.kind !== 'pitchShift') continue;
    cents += readValue(node, 'semitones') * 100 + readValue(node, 'cents');
  }
  return cents;
}

/**
 * Identity of a chain's *structure*, not its values. The engine rebuilds a chain when this
 * changes and does a smooth parameter update when it does not, which is why dragging a knob
 * never rebuilds a graph.
 */
export function chainSignature(chain: EffectChain): string {
  let signature = chain.isBypassed ? 'B' : 'A';
  for (const node of chain.nodes) {
    signature += `|${node.kind}:${node.id}:${node.isBypassed ? 1 : 0}`;
  }
  return signature;
}

// MARK: - Built units

/** Every node a unit owns, so `applyParameters` can find what it needs to change. */
interface UnitParts {
  lowShelf?: BiquadFilterNode;
  peaking?: BiquadFilterNode;
  highShelf?: BiquadFilterNode;
  filter?: BiquadFilterNode;
  compressor?: DynamicsCompressorNode;
  makeup?: GainNode;
  preGain?: GainNode;
  shaper?: WaveShaperNode;
  postGain?: GainNode;
  tone?: BiquadFilterNode;
  wet?: GainNode;
  dry?: GainNode;
  convolver?: ConvolverNode;
  preDelay?: DelayNode;
  delay?: DelayNode;
  feedback?: GainNode;
  voices?: DelayNode[];
  voiceGains?: GainNode[];
  lfos?: OscillatorNode[];
  lfoDepths?: GainNode[];
  carrier?: OscillatorNode;
  ring?: GainNode;
  limiterNode?: AudioWorkletNode;
  gain?: GainNode;
}

export interface EffectUnit {
  readonly id: UUID;
  readonly kind: EffectKind;
  readonly context: BaseAudioContext;
  /** Where audio enters this unit. */
  readonly input: GainNode;
  /** Where audio leaves it. */
  readonly output: GainNode;
  readonly parts: UnitParts;
  /** Current values, kept in step by `applyParameters` so nothing has to re-read the model. */
  readonly values: Record<string, number>;
  isBypassed: boolean;
  /** Project tempo — only the delay's sync switch uses it. */
  tempo: number;
  setBypassed(bypassed: boolean): void;
  dispose(): void;
}

export interface EffectChainGraph {
  readonly input: GainNode;
  readonly output: GainNode;
  readonly units: readonly EffectUnit[];
  /** Extra pitch, in cents, the voice must apply for this chain's `pitchShift` nodes. */
  voicePitchCents(): number;
  setBypassed(bypassed: boolean): void;
  /** Returns false when the chain has no realtime unit with that id. */
  setParameter(nodeID: UUID, key: string, value: number): boolean;
  setTempo(bpm: number): void;
  dispose(): void;
}

export interface EffectBuildOptions {
  /** True once `padlab-limiter` has been added to this context's worklet. */
  limiterWorklet?: boolean;
  /** Project tempo in BPM, for the delay's tempo-sync switch. */
  tempo?: number;
}

/** Crossfade constant for bypass and for every smoothed parameter change. */
const SMOOTHING = 0.015;
const PARAMETER_SMOOTHING = 0.02;

function readValue(node: EffectNode, key: string): number {
  const descriptor = effectKindParameter(node.kind, key);
  const raw = node.values[key];
  if (descriptor === undefined) return raw ?? 0;
  return effectParameterClamp(descriptor, raw ?? descriptor.defaultValue);
}

function decibelsToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * `setTargetAtTime` rather than an immediate assignment: a knob drag produces a value every
 * few milliseconds, and stepping an AudioParam at audio rate is a buzz.
 */
function ramp(param: AudioParam, value: number, now: number, constant = PARAMETER_SMOOTHING): void {
  if (!Number.isFinite(value)) return;
  param.setTargetAtTime(value, now, constant);
}

// MARK: - Distortion transfer curves

const CURVE_POINTS = 2049;

/**
 * Four shapes for the `character` parameter, each a unit-gain transfer function. Drive lives
 * in a pre-gain node instead of being baked into the curve, so turning the drive up does not
 * force a new 2 049-point array to be built and swapped under a sounding note.
 */
function buildDistortionCurve(character: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(CURVE_POINTS);
  for (let i = 0; i < CURVE_POINTS; i++) {
    const x = (i / (CURVE_POINTS - 1)) * 2 - 1;
    let y: number;
    switch (character) {
      case 1:
        // Hard clip with a short knee — the transistor-fuzz end.
        y = x < -0.7 ? -0.7 - 0.3 * Math.tanh((x + 0.7) * 4) : x > 0.7 ? 0.7 + 0.3 * Math.tanh((x - 0.7) * 4) : x;
        break;
      case 2: {
        // Wavefolder: past unity the signal folds back instead of flattening, which is where
        // the metallic upper harmonics come from.
        y = Math.sin(x * Math.PI * 1.5) * 0.7 + Math.sin(x * Math.PI * 0.5) * 0.3;
        break;
      }
      case 3: {
        // Asymmetric, valve-like: the negative half compresses sooner than the positive one,
        // which is what puts even harmonics in.
        y = x >= 0 ? Math.tanh(x * 1.6) : Math.tanh(x * 2.6) * 0.78;
        break;
      }
      default:
        y = Math.tanh(x * 2);
        break;
    }
    curve[i] = Math.max(-1, Math.min(1, y));
  }
  return curve;
}

const distortionCurves = new Map<number, Float32Array<ArrayBuffer>>();

function distortionCurve(character: number): Float32Array<ArrayBuffer> {
  const key = Math.max(0, Math.min(3, Math.round(character)));
  const cached = distortionCurves.get(key);
  if (cached !== undefined) return cached;
  const curve = buildDistortionCurve(key);
  distortionCurves.set(key, curve);
  return curve;
}

// MARK: - Reverb impulse responses

/** Longest tail we synthesise. 4 s of stereo float at 48 kHz is 1.5 MB — the practical limit
 * for something regenerated while a finger is on a knob. */
const MAX_REVERB_SECONDS = 4;
const REVERB_CACHE_LIMIT = 16;

const reverbCache = new Map<string, AudioBuffer>();

/** Deterministic uniform noise in [0, 1), so the same size/damping always yields the same room. */
function seededNoise(state: number): () => number {
  let value = state | 0 || 1;
  return () => {
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    return (value >>> 0) / 4294967296;
  };
}

function reverbImpulse(context: BaseAudioContext, size: number, damping: number): AudioBuffer {
  // Quantising the key means a knob drag reuses buffers instead of allocating one per frame.
  const sizeStep = Math.round(Math.max(0, Math.min(1, size)) * 20);
  const dampStep = Math.round(Math.max(0, Math.min(1, damping)) * 10);
  const key = `${context.sampleRate}:${sizeStep}:${dampStep}`;
  const cached = reverbCache.get(key);
  if (cached !== undefined) return cached;

  const normalisedSize = sizeStep / 20;
  const seconds = 0.15 + normalisedSize * normalisedSize * (MAX_REVERB_SECONDS - 0.15);
  const frames = Math.max(1, Math.floor(seconds * context.sampleRate));
  const buffer = context.createBuffer(2, frames, context.sampleRate);

  // -60 dB at the end of the tail.
  const decay = 6.908 / frames;
  const damp = dampStep / 10;
  // A one-pole low-pass whose corner closes as the tail runs on: air and soft furnishings
  // both eat treble faster than bass, and a flat-spectrum tail sounds like a metal box.
  const dampingStart = 1 - damp * 0.85;
  const earlyFrames = Math.min(frames, Math.floor(0.03 * context.sampleRate));

  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);
    const noise = seededNoise(0x9e3779b9 ^ (sizeStep << 8) ^ (dampStep << 3) ^ (channel * 7919));
    let previous = 0;
    for (let i = 0; i < frames; i++) {
      const envelope = Math.exp(-decay * i);
      const coefficient = dampingStart * (1 - (i / frames) * damp * 0.6);
      const sample = noise() * 2 - 1;
      previous = previous + coefficient * (sample - previous);
      let value = previous * envelope;
      // Sparse early reflections give the room a size you can hear before the tail arrives.
      if (i < earlyFrames && i % 137 === channel * 31) value += (noise() * 2 - 1) * 0.6 * envelope;
      data[i] = value;
    }
  }

  if (reverbCache.size >= REVERB_CACHE_LIMIT) {
    const oldest = reverbCache.keys().next();
    if (!oldest.done) reverbCache.delete(oldest.value);
  }
  reverbCache.set(key, buffer);
  return buffer;
}

// MARK: - Musical delay divisions

/** Beat fractions the sync switch snaps to, shortest first. */
const SYNC_DIVISIONS: readonly number[] = [1 / 6, 0.25, 1 / 3, 0.5, 2 / 3, 0.75, 1, 1.5, 2, 3, 4];

function syncedDelaySeconds(seconds: number, tempo: number): number {
  const beat = 60 / Math.max(20, Math.min(300, tempo));
  let best = SYNC_DIVISIONS[0]! * beat;
  let bestDistance = Math.abs(best - seconds);
  for (const division of SYNC_DIVISIONS) {
    const candidate = division * beat;
    const distance = Math.abs(candidate - seconds);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return Math.min(best, 2);
}

// MARK: - Unit construction

class Unit implements EffectUnit {
  readonly input: GainNode;
  readonly output: GainNode;
  readonly parts: UnitParts = {};
  readonly values: Record<string, number> = {};
  isBypassed = false;
  tempo = 120;

  private readonly active: GainNode;
  private readonly bypass: GainNode;
  private readonly owned: AudioNode[] = [];
  private readonly started: OscillatorNode[] = [];

  constructor(
    readonly context: BaseAudioContext,
    readonly id: UUID,
    readonly kind: EffectKind,
  ) {
    this.input = context.createGain();
    this.output = context.createGain();
    this.active = context.createGain();
    this.bypass = context.createGain();
    this.bypass.gain.value = 0;
    this.active.connect(this.output);
    this.input.connect(this.bypass);
    this.bypass.connect(this.output);
  }

  /** The node the effect internals must end on. */
  get tail(): GainNode {
    return this.active;
  }

  own<T extends AudioNode>(node: T): T {
    this.owned.push(node);
    return node;
  }

  ownOscillator(node: OscillatorNode): OscillatorNode {
    this.started.push(node);
    this.owned.push(node);
    return node;
  }

  startOscillators(): void {
    const now = this.context.currentTime;
    this.started.forEach((oscillator, index) => {
      // Staggered starts stand in for the phase offset `OscillatorNode` will not give us,
      // which is what keeps a four-voice chorus from sounding like one deep vibrato.
      oscillator.start(now + index * 0.017);
    });
  }

  setBypassed(bypassed: boolean, immediate = false): void {
    if (this.isBypassed === bypassed) return;
    this.isBypassed = bypassed;
    if (immediate) {
      // At build time there is nothing sounding to click, and an instant setting means a
      // chain that loads bypassed is silent from its very first block.
      this.active.gain.value = bypassed ? 0 : 1;
      this.bypass.gain.value = bypassed ? 1 : 0;
      return;
    }
    const now = this.context.currentTime;
    this.active.gain.setTargetAtTime(bypassed ? 0 : 1, now, SMOOTHING);
    this.bypass.gain.setTargetAtTime(bypassed ? 1 : 0, now, SMOOTHING);
  }

  dispose(): void {
    for (const oscillator of this.started) {
      try {
        oscillator.stop();
      } catch {
        // An oscillator that was never started throws; there is nothing to clean up.
      }
    }
    for (const node of this.owned) node.disconnect();
    this.input.disconnect();
    this.output.disconnect();
    this.active.disconnect();
    this.bypass.disconnect();
  }
}

/** Wires a straight series of nodes between two points. */
function series(from: AudioNode, through: readonly AudioNode[], to: AudioNode): void {
  let previous = from;
  for (const node of through) {
    previous.connect(node);
    previous = node;
  }
  previous.connect(to);
}

function buildUnit(
  context: BaseAudioContext,
  node: EffectNode,
  options: EffectBuildOptions,
): EffectUnit | null {
  if (EFFECT_REALTIME_SUPPORT[node.kind] !== 'node') return null;

  const unit = new Unit(context, node.id, node.kind);
  unit.tempo = options.tempo ?? 120;
  const parts = unit.parts;

  switch (node.kind) {
    case 'eq': {
      const low = context.createBiquadFilter();
      low.type = 'lowshelf';
      const mid = context.createBiquadFilter();
      mid.type = 'peaking';
      const high = context.createBiquadFilter();
      high.type = 'highshelf';
      parts.lowShelf = unit.own(low);
      parts.peaking = unit.own(mid);
      parts.highShelf = unit.own(high);
      series(unit.input, [low, mid, high], unit.tail);
      break;
    }
    case 'highPass':
    case 'lowPass':
    case 'bandPass': {
      const filter = context.createBiquadFilter();
      filter.type =
        node.kind === 'highPass' ? 'highpass' : node.kind === 'lowPass' ? 'lowpass' : 'bandpass';
      parts.filter = unit.own(filter);
      series(unit.input, [filter], unit.tail);
      break;
    }
    case 'compressor': {
      const compressor = context.createDynamicsCompressor();
      const makeup = context.createGain();
      parts.compressor = unit.own(compressor);
      parts.makeup = unit.own(makeup);
      series(unit.input, [compressor, makeup], unit.tail);
      break;
    }
    case 'limiter': {
      if (options.limiterWorklet === true) {
        const limiter = new AudioWorkletNode(context, 'padlab-limiter', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [2],
          channelCount: 2,
          channelCountMode: 'explicit',
          channelInterpretation: 'speakers',
        });
        parts.limiterNode = unit.own(limiter);
        series(unit.input, [limiter], unit.tail);
      } else {
        // The substitute is a hard-kneed compressor. It has no look-ahead, so it catches
        // sustained overload but lets the leading transient through — it holds an average,
        // not a ceiling, and callers that need a true ceiling must bounce.
        //
        // This branch is what the shipping engine builds: `limiterWorklet` is never passed,
        // because no `padlab-limiter` module is registered on the live context. The comment
        // that used to sit here claimed "the engine reports which of the two is running";
        // nothing reported anything, so it is stated plainly instead of promised elsewhere.
        const drive = context.createGain();
        const compressor = context.createDynamicsCompressor();
        compressor.knee.value = 0;
        compressor.ratio.value = 20;
        // The fastest the node allows. It is still a render quantum behind the transient,
        // but 1 ms was a millisecond of overshoot bought for nothing.
        compressor.attack.value = 0;
        parts.preGain = unit.own(drive);
        parts.compressor = unit.own(compressor);
        series(unit.input, [drive, compressor], unit.tail);
      }
      break;
    }
    case 'distortion': {
      const pre = context.createGain();
      const shaper = context.createWaveShaper();
      shaper.oversample = '4x';
      const tone = context.createBiquadFilter();
      tone.type = 'lowpass';
      tone.Q.value = 0.7;
      const post = context.createGain();
      const wet = context.createGain();
      const dry = context.createGain();
      parts.preGain = unit.own(pre);
      parts.shaper = unit.own(shaper);
      parts.tone = unit.own(tone);
      parts.postGain = unit.own(post);
      parts.wet = unit.own(wet);
      parts.dry = unit.own(dry);
      series(unit.input, [pre, shaper, tone, post, wet], unit.tail);
      series(unit.input, [dry], unit.tail);
      break;
    }
    case 'ringMod': {
      const carrier = context.createOscillator();
      carrier.type = 'sine';
      const ring = context.createGain();
      // Zero the offset so the oscillator *is* the gain: output = input × carrier.
      ring.gain.value = 0;
      carrier.connect(ring.gain);
      const wet = context.createGain();
      const dry = context.createGain();
      parts.carrier = unit.ownOscillator(carrier);
      parts.ring = unit.own(ring);
      parts.wet = unit.own(wet);
      parts.dry = unit.own(dry);
      series(unit.input, [ring, wet], unit.tail);
      series(unit.input, [dry], unit.tail);
      break;
    }
    case 'reverb': {
      const preDelay = context.createDelay(0.25);
      const convolver = context.createConvolver();
      convolver.normalize = true;
      const wet = context.createGain();
      const dry = context.createGain();
      parts.preDelay = unit.own(preDelay);
      parts.convolver = unit.own(convolver);
      parts.wet = unit.own(wet);
      parts.dry = unit.own(dry);
      series(unit.input, [preDelay, convolver, wet], unit.tail);
      series(unit.input, [dry], unit.tail);
      break;
    }
    case 'delay': {
      const delay = context.createDelay(2);
      const feedback = context.createGain();
      const tone = context.createBiquadFilter();
      tone.type = 'lowpass';
      const wet = context.createGain();
      const dry = context.createGain();
      parts.delay = unit.own(delay);
      parts.feedback = unit.own(feedback);
      parts.tone = unit.own(tone);
      parts.wet = unit.own(wet);
      parts.dry = unit.own(dry);
      // Tone sits inside the loop, so each repeat is darker than the last — the reason a tape
      // echo dissolves instead of piling up.
      delay.connect(tone);
      tone.connect(feedback);
      feedback.connect(delay);
      series(unit.input, [delay, wet], unit.tail);
      series(unit.input, [dry], unit.tail);
      break;
    }
    case 'chorus':
    case 'flanger': {
      const isFlanger = node.kind === 'flanger';
      const count = isFlanger ? 1 : 4;
      const delays: DelayNode[] = [];
      const gains: GainNode[] = [];
      const lfos: OscillatorNode[] = [];
      const depths: GainNode[] = [];
      const wet = context.createGain();
      const dry = context.createGain();
      for (let i = 0; i < count; i++) {
        const delay = context.createDelay(0.06);
        const gain = context.createGain();
        const lfo = context.createOscillator();
        lfo.type = 'sine';
        const depth = context.createGain();
        lfo.connect(depth);
        depth.connect(delay.delayTime);
        delays.push(unit.own(delay));
        gains.push(unit.own(gain));
        lfos.push(unit.ownOscillator(lfo));
        depths.push(unit.own(depth));
        series(unit.input, [delay, gain], wet);
      }
      if (isFlanger) {
        const feedback = context.createGain();
        parts.feedback = unit.own(feedback);
        const head = delays[0];
        if (head !== undefined) {
          head.connect(feedback);
          feedback.connect(head);
        }
      }
      parts.voices = delays;
      parts.voiceGains = gains;
      parts.lfos = lfos;
      parts.lfoDepths = depths;
      parts.wet = unit.own(wet);
      parts.dry = unit.own(dry);
      wet.connect(unit.tail);
      series(unit.input, [dry], unit.tail);
      break;
    }
    case 'tremolo': {
      const amplitude = context.createGain();
      const lfo = context.createOscillator();
      const depth = context.createGain();
      lfo.connect(depth);
      depth.connect(amplitude.gain);
      parts.gain = unit.own(amplitude);
      parts.lfos = [unit.ownOscillator(lfo)];
      parts.lfoDepths = [unit.own(depth)];
      series(unit.input, [amplitude], unit.tail);
      break;
    }
    case 'gainTrim': {
      const gain = context.createGain();
      parts.gain = unit.own(gain);
      series(unit.input, [gain], unit.tail);
      break;
    }
    default:
      // Unreachable: every 'node' kind is handled above, and the guard at the top of this
      // function already rejected the rest.
      unit.input.connect(unit.tail);
      break;
  }

  for (const descriptor of effectKindParameters(node.kind)) {
    unit.values[descriptor.key] = readValue(node, descriptor.key);
  }
  applyParameters(unit, node);
  unit.setBypassed(node.isBypassed, true);
  unit.startOscillators();
  return unit;
}

// MARK: - Parameter application

/**
 * Pushes a model node's values onto a built unit. Continuous parameters are ramped;
 * everything discrete — a filter type, a waveshaper curve, an impulse response — is assigned,
 * because there is no meaningful halfway point between two of them.
 */
export function applyParameters(unit: EffectUnit, node: EffectNode): void {
  const now = unit.context.currentTime;
  const parts = unit.parts;
  const value = (key: string): number => {
    const next = readValue(node, key);
    unit.values[key] = next;
    return next;
  };

  switch (unit.kind) {
    case 'eq': {
      const low = parts.lowShelf;
      const mid = parts.peaking;
      const high = parts.highShelf;
      if (low !== undefined) {
        ramp(low.frequency, value('lowFreq'), now);
        ramp(low.gain, value('lowGain'), now);
      }
      if (mid !== undefined) {
        ramp(mid.frequency, value('midFreq'), now);
        ramp(mid.Q, value('midQ'), now);
        ramp(mid.gain, value('midGain'), now);
      }
      if (high !== undefined) {
        ramp(high.frequency, value('highFreq'), now);
        ramp(high.gain, value('highGain'), now);
      }
      return;
    }
    case 'highPass':
    case 'lowPass': {
      const filter = parts.filter;
      if (filter === undefined) return;
      ramp(filter.frequency, value('frequency'), now);
      // For low-pass and high-pass the Web Audio spec reads Q in decibels, which is exactly
      // what the model's `resonance` parameter already is.
      ramp(filter.Q, value('resonance'), now);
      return;
    }
    case 'bandPass': {
      const filter = parts.filter;
      if (filter === undefined) return;
      ramp(filter.frequency, value('frequency'), now);
      const octaves = Math.max(0.05, value('bandwidth'));
      const ratio = Math.pow(2, octaves);
      ramp(filter.Q, Math.sqrt(ratio) / (ratio - 1), now);
      return;
    }
    case 'compressor': {
      const compressor = parts.compressor;
      if (compressor !== undefined) {
        ramp(compressor.threshold, value('threshold'), now);
        ramp(compressor.ratio, value('ratio'), now);
        ramp(compressor.attack, value('attack'), now);
        ramp(compressor.release, value('release'), now);
      }
      if (parts.makeup !== undefined) ramp(parts.makeup.gain, decibelsToGain(value('makeup')), now);
      return;
    }
    case 'limiter': {
      const ceiling = value('ceiling');
      const release = value('release');
      const drive = value('drive');
      const limiter = parts.limiterNode;
      if (limiter !== undefined) {
        const ceilingParam = limiter.parameters.get('ceiling');
        const releaseParam = limiter.parameters.get('release');
        const driveParam = limiter.parameters.get('drive');
        if (ceilingParam !== undefined) ramp(ceilingParam, ceiling, now);
        if (releaseParam !== undefined) ramp(releaseParam, release, now);
        if (driveParam !== undefined) ramp(driveParam, drive, now);
        return;
      }
      if (parts.preGain !== undefined) ramp(parts.preGain.gain, decibelsToGain(drive), now);
      if (parts.compressor !== undefined) {
        ramp(parts.compressor.threshold, ceiling, now);
        ramp(parts.compressor.release, release, now);
      }
      return;
    }
    case 'distortion': {
      const drive = value('drive');
      const mix = value('mix');
      const character = value('character');
      if (parts.shaper !== undefined) {
        const curve = distortionCurve(character);
        if (parts.shaper.curve !== curve) parts.shaper.curve = curve;
      }
      if (parts.preGain !== undefined) ramp(parts.preGain.gain, 1 + drive * 23, now);
      // Compensating on the way out keeps the drive knob a texture control rather than a
      // volume control, which is the difference between usable and dangerous.
      if (parts.postGain !== undefined) ramp(parts.postGain.gain, 1 / (1 + drive * 2.2), now);
      if (parts.tone !== undefined) {
        ramp(parts.tone.frequency, 400 * Math.pow(45, value('tone')), now);
      }
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix, now);
      return;
    }
    case 'ringMod': {
      const mix = value('mix');
      if (parts.carrier !== undefined) ramp(parts.carrier.frequency, value('frequency'), now);
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix, now);
      return;
    }
    case 'reverb': {
      const size = value('size');
      const damping = value('damping');
      const mix = value('mix');
      if (parts.convolver !== undefined) {
        const impulse = reverbImpulse(unit.context, size, damping);
        // Swapping an impulse response restarts the tail, so only do it when the quantised
        // room has genuinely changed.
        if (parts.convolver.buffer !== impulse) parts.convolver.buffer = impulse;
      }
      if (parts.preDelay !== undefined) ramp(parts.preDelay.delayTime, value('preDelay'), now);
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix * 0.6, now);
      return;
    }
    case 'delay': {
      const mix = value('mix');
      const time = value('time');
      const sync = value('sync');
      if (parts.delay !== undefined) {
        const target = sync >= 0.5 ? syncedDelaySeconds(time, unit.tempo) : time;
        ramp(parts.delay.delayTime, target, now, 0.05);
      }
      if (parts.feedback !== undefined) ramp(parts.feedback.gain, value('feedback'), now);
      if (parts.tone !== undefined) ramp(parts.tone.frequency, value('tone'), now);
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix * 0.5, now);
      return;
    }
    case 'chorus': {
      const rate = value('rate');
      const depth = value('depth');
      const voices = Math.max(1, Math.round(value('voices')));
      const mix = value('mix');
      const delays = parts.voices ?? [];
      const gains = parts.voiceGains ?? [];
      const lfos = parts.lfos ?? [];
      const depths = parts.lfoDepths ?? [];
      for (let i = 0; i < delays.length; i++) {
        const on = i < voices;
        const gain = gains[i];
        if (gain !== undefined) ramp(gain.gain, on ? 1 / Math.sqrt(voices) : 0, now);
        const delay = delays[i];
        if (delay !== undefined) ramp(delay.delayTime, 0.011 + i * 0.0037, now, 0.05);
        const lfo = lfos[i];
        // Slightly different rates per voice: identical rates phase-lock into vibrato.
        if (lfo !== undefined) ramp(lfo.frequency, rate * (1 + i * 0.11), now);
        const modulation = depths[i];
        if (modulation !== undefined) ramp(modulation.gain, depth * 0.0045, now);
      }
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix * 0.5, now);
      return;
    }
    case 'flanger': {
      const rate = value('rate');
      const depth = value('depth');
      const mix = value('mix');
      const delay = parts.voices?.[0];
      if (delay !== undefined) ramp(delay.delayTime, 0.0016 + depth * 0.0022, now, 0.05);
      const lfo = parts.lfos?.[0];
      if (lfo !== undefined) ramp(lfo.frequency, rate, now);
      const modulation = parts.lfoDepths?.[0];
      if (modulation !== undefined) ramp(modulation.gain, depth * 0.0022, now);
      if (parts.feedback !== undefined) ramp(parts.feedback.gain, value('feedback'), now);
      if (parts.wet !== undefined) ramp(parts.wet.gain, mix, now);
      if (parts.dry !== undefined) ramp(parts.dry.gain, 1 - mix * 0.5, now);
      return;
    }
    case 'tremolo': {
      const depth = value('depth');
      const shape = Math.round(value('shape'));
      const lfo = parts.lfos?.[0];
      if (lfo !== undefined) {
        ramp(lfo.frequency, value('rate'), now);
        const type: OscillatorType = shape === 1 ? 'triangle' : shape === 2 ? 'square' : 'sine';
        if (lfo.type !== type) lfo.type = type;
      }
      // The carrier sits at 1 − depth/2 and the LFO swings ±depth/2, so full depth reaches
      // silence and no depth is exactly unity.
      if (parts.gain !== undefined) ramp(parts.gain.gain, 1 - depth * 0.5, now);
      const modulation = parts.lfoDepths?.[0];
      if (modulation !== undefined) ramp(modulation.gain, depth * 0.5, now);
      return;
    }
    case 'gainTrim': {
      if (parts.gain !== undefined) ramp(parts.gain.gain, decibelsToGain(value('gain')), now);
      return;
    }
    default:
      return;
  }
}

// MARK: - Chain

class ChainGraph implements EffectChainGraph {
  readonly input: GainNode;
  readonly output: GainNode;
  readonly units: EffectUnit[] = [];

  private readonly active: GainNode;
  private readonly bypass: GainNode;
  private readonly nodesByID = new Map<UUID, EffectNode>();
  private readonly unitsByID = new Map<UUID, EffectUnit>();
  private readonly pitchNodes: EffectNode[] = [];
  /** Live pitch values, keyed `nodeID/parameter`, so a knob drag reaches the next trigger. */
  private readonly pitchOverrides = new Map<string, number>();
  private bypassed = false;
  private disposed = false;

  constructor(
    private readonly context: BaseAudioContext,
    chain: EffectChain,
    options: EffectBuildOptions,
  ) {
    this.input = context.createGain();
    this.output = context.createGain();
    this.active = context.createGain();
    this.bypass = context.createGain();
    this.bypass.gain.value = 0;
    this.active.connect(this.output);
    this.input.connect(this.bypass);
    this.bypass.connect(this.output);

    let previous: AudioNode = this.input;
    for (const node of chain.nodes) {
      this.nodesByID.set(node.id, node);
      if (node.kind === 'pitchShift') {
        this.pitchNodes.push(node);
        continue;
      }
      const unit = buildUnit(context, node, options);
      if (unit === null) continue;
      previous.connect(unit.input);
      previous = unit.output;
      this.units.push(unit);
      this.unitsByID.set(node.id, unit);
    }
    previous.connect(this.active);
    if (chain.isBypassed) {
      this.bypassed = true;
      this.active.gain.value = 0;
      this.bypass.gain.value = 1;
    }
  }

  voicePitchCents(): number {
    if (this.bypassed) return 0;
    let cents = 0;
    for (const node of this.pitchNodes) {
      if (node.isBypassed) continue;
      cents +=
        (this.liveValue(node, 'semitones') ?? readValue(node, 'semitones')) * 100 +
        (this.liveValue(node, 'cents') ?? readValue(node, 'cents'));
    }
    return cents;
  }

  private liveValue(node: EffectNode, key: string): number | null {
    return this.pitchOverrides.get(`${node.id}/${key}`) ?? null;
  }

  setBypassed(bypassed: boolean): void {
    if (this.bypassed === bypassed) return;
    this.bypassed = bypassed;
    const now = this.context.currentTime;
    this.active.gain.setTargetAtTime(bypassed ? 0 : 1, now, SMOOTHING);
    this.bypass.gain.setTargetAtTime(bypassed ? 1 : 0, now, SMOOTHING);
  }

  setParameter(nodeID: UUID, key: string, value: number): boolean {
    const node = this.nodesByID.get(nodeID);
    if (node === undefined) return false;

    if (node.kind === 'pitchShift') {
      const descriptor = effectKindParameter('pitchShift', key);
      if (descriptor === undefined) return false;
      this.pitchOverrides.set(`${nodeID}/${key}`, effectParameterClamp(descriptor, value));
      return true;
    }

    const unit = this.unitsByID.get(nodeID);
    if (unit === undefined) return false;
    const descriptor = effectKindParameter(node.kind, key);
    if (descriptor === undefined) return false;

    // The model object stays untouched — the project is owned elsewhere — so the new value is
    // pushed through a throwaway view of the node.
    const view: EffectNode = {
      id: node.id,
      kind: node.kind,
      isBypassed: node.isBypassed,
      values: { ...node.values, ...unit.values, [key]: effectParameterClamp(descriptor, value) },
    };
    applyParameters(unit, view);
    return true;
  }

  setTempo(bpm: number): void {
    for (const unit of this.units) {
      unit.tempo = bpm;
      if (unit.kind !== 'delay') continue;
      const node = this.nodesByID.get(unit.id);
      if (node !== undefined) {
        applyParameters(unit, { ...node, values: { ...node.values, ...unit.values } });
      }
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const unit of this.units) unit.dispose();
    this.input.disconnect();
    this.output.disconnect();
    this.active.disconnect();
    this.bypass.disconnect();
  }
}

/**
 * Builds a chain. Offline-only kinds contribute nothing to the graph — they are rendered into
 * the clip by the worker — and `pitchShift` contributes nothing either, because the voice
 * applies it. The result is always a valid in/out pair, so an all-offline chain is a
 * pass-through rather than a hole in the strip.
 */
export function buildEffectChain(
  context: BaseAudioContext,
  chain: EffectChain,
  options: EffectBuildOptions = {},
): EffectChainGraph {
  return new ChainGraph(context, chain, options);
}
